import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, Variants } from "framer-motion";
import {
  Stethoscope,
  Droplet,
  HeartPulse,
  AlertCircle,
  Activity,
  MapPin,
  ShieldPlus,
  Heart,
} from "lucide-react";

import img from "../../assets/Blood donation/bloodimg.jpg";
import img1 from "../../assets/Blood donation/drives.jpg";
import img2 from "../../assets/Blood donation/img3.jpg";
import img3 from "../../assets/Blood donation/mentalhealth.png";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/medical-blood-donation`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Medical & Blood Donation Support | SEVAA Foundation",
  alternateName: "Health Cannot Wait - Healthcare and Blood Donation Support",
  url: pageUrl,
  inLanguage: "en-IN",
  description:
    "SEVAA Foundation supports healthcare access through free medical camps, preventive health checkups, blood donation drives, critical medical support and health awareness for poor, tribal and rural communities.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
    image: defaultImage,
    slogan: "सेवा परमो धर्मः",
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "Place", name: "Jharkhand" },
      { "@type": "Place", name: "Chatra" },
      { "@type": "Place", name: "Pakur" },
    ],
  },
  about: [
    "Healthcare Support",
    "Medical Camps",
    "Blood Donation",
    "Free Health Checkup",
    "Preventive Healthcare",
    "Rural Healthcare",
    "Tribal Healthcare",
    "Critical Medical Support",
    "Surgery Support",
    "Maternal Health",
    "Health Awareness"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${pageUrl}#service`,
  name: "Medical Support and Blood Donation Program",
  serviceType: "Healthcare Support and Blood Donation",
  url: pageUrl,
  provider: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
  },
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  description:
    "SEVAA Foundation organizes free medical camps, blood donation drives, preventive health checkups, health awareness and critical medical support for poor, tribal, rural and vulnerable communities.",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Medical & Blood Donation",
      item: pageUrl,
    },
  ],
};

const MedicalBloodDonation: React.FC = () => {
  const staggerContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const gridItemVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <Helmet>
        <title>Medical & Blood Donation NGO | SEVAA Foundation</title>

        <meta
          name="title"
          content="Medical & Blood Donation NGO | SEVAA Foundation"
        />

        <meta
          name="description"
          content="Support SEVAA Foundation's healthcare mission through free medical camps, blood donation drives, health checkups, critical care and rural healthcare support."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Medical & Blood Donation NGO | SEVAA Foundation" />
        <meta property="og:description" content="Support free health camps, blood donation drives, critical medical support and healthcare access for poor, tribal and rural communities." />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation healthcare and blood donation support program" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Medical & Blood Donation NGO | SEVAA Foundation" />
        <meta name="twitter:description" content="Support SEVAA Foundation's healthcare mission through medical camps, blood donation, preventive care and critical support." />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation healthcare and blood donation support program" />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-poppins pb-20">
        {/* 1. HERO SECTION */}
        <section
          aria-labelledby="medical-blood-heading"
          className="relative w-full h-[400px] md:h-[500px]"
        >
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1615461066841-6116e61058f4?q=80&w=2083&auto=format&fit=crop"
              alt="SEVAA Foundation healthcare support medical camps and blood donation mission"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-black/60"></div>
          </div>

          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <span className="inline-block py-1 px-3 rounded-full bg-red-500/20 text-red-400 font-bold text-xs uppercase tracking-widest mb-4 border border-red-500/30">
                Health Cannot Wait
              </span>

              <h1
                id="medical-blood-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
              >
                Healthcare in India <br />
                <span className="text-red-400">
                  Gaps That Hurt the Most
                </span>
              </h1>

              <p className="text-teal-50 text-base md:text-lg font-medium leading-relaxed mb-8 max-w-2xl opacity-90">
                Healthcare access has improved, but large gaps remain,
                particularly for people living in poverty, tribal regions and
                remote rural areas.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2. THE HARSH REALITY */}
        <section
          aria-labelledby="healthcare-reality-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 -mt-20 relative z-10 mb-20"
        >
          <h2 id="healthcare-reality-heading" className="sr-only">
            Healthcare Access Reality in India
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-rose-500 group transition-all duration-300"
            >
              <AlertCircle className="w-12 h-12 text-rose-500 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-2xl font-black text-slate-900 mb-2">
                Millions Unreached
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Many vulnerable families lack access to basic healthcare,
                clean water, timely checkups and affordable treatment,
                especially in remote rural and tribal regions.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-amber-500 group transition-all duration-300"
            >
              <Activity className="w-12 h-12 text-amber-500 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-2xl font-black text-slate-900 mb-2">
                Nutrition Link
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Poor nutrition leads to weakened immunity, reduced learning
                capacity, low energy and increased vulnerability to preventable
                diseases.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-teal-800 p-8 rounded-3xl shadow-xl border border-teal-700 text-white border-b-[6px] border-b-teal-400 group transition-all duration-300"
            >
              <MapPin className="w-12 h-12 text-teal-300 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-2xl font-black mb-2">Access Barriers</h3>

              <p className="text-teal-100 font-medium text-sm leading-relaxed">
                Medical care is limited in poor regions due to distance, high
                costs, low awareness or lack of nearby healthcare facilities.
              </p>
            </motion.article>
          </div>
        </section>

        {/* 3. THE GROUND REALITY */}
        <section
          aria-labelledby="health-awareness-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 py-16"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3">
                <span className="w-12 h-[2px] bg-red-500"></span>
                <span className="text-red-600 font-bold text-xs uppercase tracking-widest">
                  Why Awareness Matters
                </span>
              </div>

              <h2
                id="health-awareness-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.2]"
              >
                Health underpins every opportunity in life
              </h2>

              <p className="text-slate-600 leading-relaxed font-medium">
                Preventive screenings, vaccinations, maternal health services,
                nutrition awareness and timely medical guidance are foundational
                to a thriving society.
              </p>

              <p className="text-slate-600 leading-relaxed font-medium">
                Without good health, education and economic participation become
                much harder to achieve. SEVAA Foundation bridges the gap between
                healthcare facilities and vulnerable communities.
              </p>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-2xl mt-8">
                <p className="text-slate-800 font-bold italic">
                  “SEVAA Foundation&apos;s healthcare focus ensures families are
                  healthier and children are better nourished, protected and
                  ready to learn.”
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src={img}
                alt="SEVAA Foundation healthcare support for poor and rural communities"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        {/* 4. SEVAA'S INTERVENTION */}
        <section
          aria-labelledby="medical-strategy-heading"
          className="bg-white py-24 border-t border-slate-200 mt-10"
        >
          <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2
                id="medical-strategy-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 mb-4"
              >
                Medical & Donation Strategy
              </h2>

              <p className="text-slate-500 font-medium text-lg">
                Ensuring accessible healthcare, preventive checkups, medical
                awareness, critical care support and emergency blood supply for
                people in need.
              </p>
            </div>

            <motion.div
              variants={staggerContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr"
            >
              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=2070"
                  alt="Free medical camp and preventive health checkup by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-teal-500/10 hover:border-teal-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-teal-50 transition-colors">
                  <Stethoscope className="w-7 h-7 text-teal-600" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Health Camps
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Organizing free medical camps, health awareness sessions,
                  preventive screenings and vaccination support in remote
                  villages and underserved communities.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img1}
                  alt="Blood donation drive organized by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-red-500/10 hover:border-red-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-red-50 transition-colors">
                  <Droplet className="w-7 h-7 text-red-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Blood Drives
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Conducting blood donation camps to support emergency blood
                  requirements for needy patients, accident cases, surgeries and
                  vulnerable families.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img2}
                  alt="Critical medical support and surgery assistance by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-orange-500/10 hover:border-orange-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-orange-50 transition-colors">
                  <HeartPulse className="w-7 h-7 text-orange-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Critical Support
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Providing guidance, referral support and financial assistance
                  for surgeries, emergency care and life-saving medical
                  treatments.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img3}
                  alt="Maternal health mental health and wellness awareness by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-red-700 p-8 rounded-[2rem] shadow-lg flex flex-col justify-center hover:-translate-y-2 hover:shadow-red-700/40 transition-all min-h-[350px] text-white overflow-hidden relative"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-red-600 rounded-full blur-2xl opacity-50"></div>

                <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <ShieldPlus className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-xl font-bold mb-3">Our Vision</h3>

                <p className="text-red-50 font-medium text-sm leading-relaxed mb-6">
                  Good health is the foundation of a thriving society. SEVAA
                  Foundation works to protect lives through healthcare access,
                  awareness and timely support.
                </p>

                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white text-red-700 font-bold px-6 py-2.5 rounded-full transition-colors text-sm w-fit"
                  aria-label="Donate blood or support SEVAA Foundation medical mission"
                >
                  Donate Blood
                  <Heart className="w-4 h-4" />
                </a>
              </motion.article>
            </motion.div>
          </div>
        </section>

      </main>
    </>
  );
};

export default MedicalBloodDonation;